import { Upload, Users } from "lucide-react";
import type { CampaignPlan } from "@shared-core/data/api-types";
import { Button } from "../../app/components/primitives/button";

/**
 * The tiers of a campaign: how many people in its audience are in each,
 * with "Add people and tiers" and "View people". Results for every channel
 * live on Marketing › Results, so there is no separate link here. Shown only
 * on a campaign whose page shows its plan (see showsCampaignPlan).
 */
export function TiersCard({
  plan,
  onAddPeople,
  onViewPeople,
}: {
  plan: CampaignPlan;
  onAddPeople: () => void;
  onViewPeople: () => void;
}) {
  const audienceName = plan.audience_name ?? "campaign's";
  return (
    <section
      aria-labelledby="campaign-tiers-heading"
      className="rounded-lg border border-border bg-surface px-4 py-3 space-y-3"
    >
      <h2 id="campaign-tiers-heading" className="text-lg font-semibold">
        Tiers
      </h2>
      {plan.tiers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tiers yet. Tiers are only needed when different people get
          different things, such as a mailing to some of them. To set them,
          upload a list with a tier column; anyone not yet in the {audienceName}{" "}
          audience is added to it.
        </p>
      ) : plan.person_count === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nobody has a tier yet. Upload your list with a tier column (
          {plan.tiers.map((t) => t.name).join(", ")}). Mailings and call rounds
          can't go to anyone until people have a tier.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2 text-sm">
          {plan.tiers.map((tier) => (
            <li
              key={tier.id}
              className="rounded-md border border-border bg-background px-3 py-1.5"
            >
              <span className="font-medium text-foreground">{tier.name}</span>{" "}
              <span className="text-muted-foreground tabular-nums">
                {tier.person_count}{" "}
                {tier.person_count === 1 ? "person" : "people"}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onAddPeople}>
          <Upload className="size-4" />
          Add people and tiers
        </Button>
        {plan.person_count > 0 && (
          <Button variant="outline" size="sm" onClick={onViewPeople}>
            <Users className="size-4" />
            View people ({plan.person_count})
          </Button>
        )}
      </div>
    </section>
  );
}
